import React from "react";
import { Box, IconButton, Slider, Typography, useTheme } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { VideoData } from "../../../shared/interfaces/VideoData";
import { VideoController } from "../hooks/useVideoController";
import { formatTimestamp } from "../utils/timeFormat";
import audioThumbnailLight from "../../../assets/audio_thumbnail_light.svg";
import audioThumbnailDark from "../../../assets/audio_thumbnail_dark.svg";

interface EditorVideoPlayerProps {
  video: VideoData;
  controller: VideoController;
  /** Custom scrubber (the TimeRangeSelector in Phase 2). Defaults to a seek slider. */
  scrubber?: React.ReactNode;
}

/**
 * Lightweight native-<video> player for the transcript editor. Bound to a
 * shared {@link VideoController} so the transcript list and range selector read
 * the same playback time and can seek. Deliberately not the heavy VideoPreview
 * (which is coupled to evidence cards, overlays, and downloads).
 */
export const EditorVideoPlayer: React.FC<EditorVideoPlayerProps> = ({
  video,
  controller,
  scrubber,
}) => {
  const theme = useTheme();
  const isAudio = video.fileType?.startsWith("audio") ?? false;
  const audioPoster =
    theme.palette.mode === "dark" ? audioThumbnailDark : audioThumbnailLight;
  const { currentTime, duration, isPlaying, videoRef, bind, togglePlay, seekTo } =
    controller;

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: theme.palette.common.black,
      }}
    >
      <Box
        sx={{
          position: "relative",
          aspectRatio: "16 / 9",
          backgroundColor: theme.palette.common.black,
        }}
      >
        <video
          ref={videoRef}
          src={video.videoUrl}
          poster={isAudio ? audioPoster : undefined}
          onClick={togglePlay}
          playsInline
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "contain",
            cursor: "pointer",
          }}
          {...bind}
        />
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Box display="flex" flexDirection="column" gap={0.5}>
          {/* Playback control + duration, stacked on top of the full-width time controls. */}
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <Typography
              variant="caption"
              sx={{ fontFamily: "monospace", color: "text.secondary", minWidth: 92 }}
            >
              {formatTimestamp(currentTime)} / {formatTimestamp(duration)}
            </Typography>
          </Box>
          <Box sx={{ width: "100%" }}>
            {scrubber ?? (
              <Slider
                size="small"
                value={Math.min(currentTime, duration || 0)}
                min={0}
                max={duration || 100}
                step={0.01}
                onChange={(_, v) => seekTo(Array.isArray(v) ? v[0] : v)}
                aria-label="Seek"
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
